/** The Overview's setup checklist (plan §7.3 Overview) and Get started's
 *  steps 3b, 4 and 5 (plan §7.3 Get started), computed from what exists on
 *  this cloud today:
 *  - Private access provides sign-in, the first private-app connection (a
 *    connector) and access policies;
 *  - the SASE overview read (`GET /sase/overview`, package C2) provides the
 *    internet on-ramps in use, the one policy's publish state, the recommended
 *    baseline's categories, the inspection certificate and the traffic seen.
 *  A step whose source is not there for this organisation says why and points
 *  to Get started instead of showing a number (SD-17). Every link stays inside
 *  the SASE section (SASE-18). */
import type { AccessPolicy, Connector, Org } from "@/features/privateAccess/types";
import { accessTabPath, SASE_BASE } from "@/lib/sectionBase";

import type { BaselineCategory, SaseOverview } from "./overview/types";
import type { ChecklistItem, ChecklistState } from "./types";

export interface ChecklistInput {
  /** Private access is on in the SASE section (sase AND its module flag). */
  privateAccess: boolean;
  org: Org | null;
  connectors: Connector[];
  policies: AccessPolicy[];
  /** The SASE overview read; `null` while loading or when it failed. */
  overview?: SaseOverview | null;
  /** SASE → Analytics is on (its module flag). */
  analytics?: boolean;
}

export const GET_STARTED = "/sase/get-started";
/** SASE → Connections (plan §7.3; the client, sites, proxy and DNS). */
export const CONNECTIONS = "/sase/connections";
/** SASE → Settings (plan §7.3; the inspection certificate download). */
export const SETTINGS = "/sase/settings";
export const SASE_POLICIES = "/sase/policies";
export const POLICY_TESTER = "/sase/policies?tab=tester";
export const INTERNET_ACCESS = "/sase/internet";
export const DECRYPTION = "/sase/internet?tab=decryption";
export const ANALYTICS = "/sase/analytics";

// Every step links inside the SASE section (SD-01, SASE-18): Private access's
// SASE door, its identity pages under Users and devices.
const PRIVATE_ACCESS = SASE_BASE.privateAccess;
const ACCESS_GET_STARTED = `${SASE_BASE.privateAccess}/get-started`;
const SIGN_IN = accessTabPath(SASE_BASE, "sign-in");
const CONNECTORS = accessTabPath(SASE_BASE, "connectors");
const POLICIES = accessTabPath(SASE_BASE, "policies");

const NOT_ON_THIS_CLOUD = "Comes with Private access, which is not switched on for this cloud yet.";

const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;

/** One step of Get started, read from the overview. */
export interface StepState {
  state: ChecklistState;
  detail: string;
}

/** Step 3b: an internet on-ramp in use (the client, a site tunnel, DNS). */
export function internetConnectionStep(overview: SaseOverview | null | undefined): StepState {
  const c = overview?.connections;
  if (!c) return { state: "later", detail: "Loading your connections." };
  if (c.status === "unavailable")
    return { state: "later", detail: c.reason ?? "Connections are not available yet." };
  const parts: string[] = [];
  if (c.client_devices > 0)
    parts.push(plural(c.client_devices, "device has the client", "devices have the client"));
  if (c.sites_up > 0) parts.push(plural(c.sites_up, "site tunnel is up", "site tunnels are up"));
  if (c.dns_sources > 0)
    parts.push(plural(c.dns_sources, "DNS source is registered", "DNS sources are registered"));
  if (parts.length) return { state: "done", detail: `${parts.join(", ")}.` };
  if (c.sites > 0)
    return {
      state: "todo",
      detail: `${plural(c.sites, "site is", "sites are")} set up but no tunnel is up yet. Configure your router with its settings.`,
    };
  return {
    state: "todo",
    detail:
      "Install the client on a device, connect an office with a site tunnel, point browsers at the web proxy or register your DNS source addresses.",
  };
}

export interface BaselineStep extends StepState {
  /** Recommended categories not blocked yet (what the one click blocks). */
  missing: BaselineCategory[];
  /** Everything is blocked in the draft but not published yet. */
  needsPublish: boolean;
}

/** Step 4: the recommended baseline's category blocks, in force. */
export function baselineStep(overview: SaseOverview | null | undefined): BaselineStep {
  const b = overview?.baseline;
  if (!b)
    return { state: "later", detail: "Loading your policy.", missing: [], needsPublish: false };
  if (b.status === "unavailable")
    return {
      state: "later",
      detail: b.reason ?? "Your policy is not available yet.",
      missing: [],
      needsPublish: false,
    };
  if (b.missing.length > 0)
    return {
      state: "todo",
      detail: `${b.in_force} of ${b.recommended} recommended categories are blocked.`,
      missing: b.missing,
      needsPublish: false,
    };
  if (b.published_version === null || b.unpublished_changes)
    return {
      state: "todo",
      detail:
        "Every recommended category is blocked in your draft. Publish the policy to put it in force.",
      missing: [],
      needsPublish: true,
    };
  return {
    state: "done",
    detail: `The ${b.recommended} recommended categories are blocked and in force (version ${b.published_version}).`,
    missing: [],
    needsPublish: false,
  };
}

export interface TestStep extends StepState {
  /** The tester needs a policy to test. */
  canTest: boolean;
}

/** Step 5: the tester, and a block seen in the organisation's activity. */
export function testStep(overview: SaseOverview | null | undefined): TestStep {
  const t = overview?.tester;
  if (!t) return { state: "later", detail: "Loading your policy.", canTest: false };
  if (t.status === "unavailable")
    return {
      state: "later",
      detail: t.reason ?? "The tester is not available yet.",
      canTest: false,
    };
  if (!t.policy_id)
    return {
      state: "todo",
      detail: "Write your policy first (step 4 starts one), then test it here.",
      canTest: false,
    };
  if (t.blocked_7d && t.blocked_7d > 0)
    return {
      state: "done",
      detail: `Your policy stopped ${plural(t.blocked_7d, "request", "requests")} in the last 7 days.`,
      canTest: true,
    };
  return {
    state: "todo",
    detail:
      t.blocked_7d === null
        ? "Ask the tester what happens to a user, a device and an address. Blocks show here once your activity is collected."
        : "Ask the tester what happens to a user, a device and an address, then open a blocked site from a connected device: the block shows here.",
    canTest: true,
  };
}

export function buildChecklist(input: ChecklistInput): ChecklistItem[] {
  const { privateAccess, org } = input;
  const healthy = input.connectors.filter((c) => c.status === "healthy").length;
  const inForce = input.policies.filter((p) => !p.preview).length;

  const signIn: ChecklistItem = !privateAccess
    ? {
        key: "sign_in",
        title: "Connect sign-in",
        detail: NOT_ON_THIS_CLOUD,
        state: "later",
        to: GET_STARTED,
        linkLabel: "See the steps",
      }
    : !org
      ? {
          key: "sign_in",
          title: "Connect sign-in",
          detail: "Create your organisation: it is where your people sign in.",
          state: "todo",
          to: PRIVATE_ACCESS,
          linkLabel: "Create the organisation",
        }
      : !org.sign_in_url
        ? {
            key: "sign_in",
            title: "Connect sign-in",
            detail:
              "Your organisation is being set up; its sign-in address shows here when it is ready.",
            state: "todo",
            to: PRIVATE_ACCESS,
            linkLabel: "Open Private access",
          }
        : {
            key: "sign_in",
            title: "Connect sign-in",
            detail: `Your people sign in at ${org.sign_in_url}.`,
            state: "done",
            to: SIGN_IN,
            linkLabel: "Sign-in settings",
          };

  const connection = connectionItem(input, healthy);
  const policy = policyItem(input, inForce);
  const certificate = certificateItem(input.overview);
  const traffic = trafficItem(input.overview, Boolean(input.analytics));

  return [signIn, connection, policy, certificate, traffic];
}

function connectionItem(input: ChecklistInput, healthy: number): ChecklistItem {
  const base = { key: "connection" as const, title: "Bring up a connection" };
  if (input.privateAccess && healthy > 0)
    return {
      ...base,
      detail:
        healthy === 1
          ? "A connector is healthy: your private apps are reachable through it."
          : `${healthy} connectors are healthy: your private apps are reachable through them.`,
      state: "done",
      to: CONNECTORS,
      linkLabel: "Connectors",
    };
  const internet = input.overview ? internetConnectionStep(input.overview) : null;
  if (internet?.state === "done")
    return {
      ...base,
      detail: internet.detail,
      state: "done",
      to: CONNECTIONS,
      linkLabel: "Connections",
    };
  if (input.privateAccess)
    return {
      ...base,
      detail: "Start a connector next to your apps. It only makes outbound connections.",
      state: "todo",
      to: ACCESS_GET_STARTED,
      linkLabel: "Start a connector",
    };
  if (internet?.state === "todo")
    return {
      ...base,
      detail: internet.detail,
      state: "todo",
      to: CONNECTIONS,
      linkLabel: "Connect",
    };
  return {
    ...base,
    detail: internet?.detail && input.overview ? internet.detail : NOT_ON_THIS_CLOUD,
    state: "later",
    to: GET_STARTED,
    linkLabel: "See the steps",
  };
}

function policyItem(input: ChecklistInput, inForce: number): ChecklistItem {
  const base = { key: "policy" as const, title: "Put a policy in force" };
  if (input.privateAccess && inForce > 0)
    return {
      ...base,
      detail:
        inForce === 1
          ? "An access policy is in force."
          : `${inForce} access policies are in force.`,
      state: "done",
      to: POLICIES,
      linkLabel: "Access policies",
    };
  const b = input.overview?.baseline;
  const sase = b && b.status !== "unavailable" ? b : null;
  if (sase?.published_version)
    return {
      ...base,
      // In force is in force: the step is done. Changes waiting in the
      // draft are said here too, in the words of the posture check that
      // counts them as a gap ("Publish the policy and its changes"), so the
      // two never read as opposite answers to one question (round 4).
      detail: sase.unpublished_changes
        ? `Version ${sase.published_version} of your policy is in force. The draft has changes that are not published yet.`
        : `Version ${sase.published_version} of your policy is in force.`,
      state: "done",
      to: SASE_POLICIES,
      linkLabel: "Policies",
    };
  if (input.privateAccess)
    return {
      ...base,
      detail: "Write an access policy: nobody reaches a private app until one lets them in.",
      state: "todo",
      to: POLICIES,
      linkLabel: "Write a policy",
    };
  if (sase)
    return {
      ...base,
      detail: "Publish your policy: nothing is enforced until a version is published.",
      state: "todo",
      to: SASE_POLICIES,
      linkLabel: "Open Policies",
    };
  return {
    ...base,
    detail: b?.reason ?? NOT_ON_THIS_CLOUD,
    state: "later",
    to: GET_STARTED,
    linkLabel: "See the steps",
  };
}

function certificateItem(overview: SaseOverview | null | undefined): ChecklistItem {
  const base = { key: "certificate" as const, title: "Deploy the inspection certificate" };
  const c = overview?.certificate;
  if (!c || c.status !== "ok")
    return {
      ...base,
      detail: c?.reason
        ? `Lets Internet access look inside encrypted web traffic. ${c.reason}`
        : "Lets Internet access look inside encrypted web traffic. It is not available for your organisation yet.",
      state: "later",
      to: GET_STARTED,
      linkLabel: "See the steps",
    };
  if (c.decrypting)
    return {
      ...base,
      detail:
        "Decryption is on: your people's devices must trust the certificate, or secure sites show warnings.",
      state: "done",
      to: DECRYPTION,
      linkLabel: "Decryption",
    };
  return {
    ...base,
    detail:
      "Download the certificate, install it on your people's devices, then switch decryption on.",
    state: "todo",
    to: SETTINGS,
    linkLabel: "Download the certificate",
  };
}

function trafficItem(overview: SaseOverview | null | undefined, analytics: boolean): ChecklistItem {
  const base = { key: "traffic" as const, title: "See your first traffic" };
  const t = overview?.traffic;
  if (!t || t.status === "unavailable")
    return {
      ...base,
      detail: t?.reason ?? "Shows here once your organisation's traffic is collected.",
      state: "later",
      to: GET_STARTED,
      linkLabel: "See the steps",
    };
  if (t.status === "empty")
    return {
      ...base,
      detail: "No traffic in the last 7 days: connect a device or a site first.",
      state: "todo",
      to: CONNECTIONS,
      linkLabel: "Connect",
    };
  const detail =
    t.events_7d && t.events_7d > 0
      ? `${plural(t.events_7d, "request", "requests")} in the last 7 days.`
      : `The security nodes have counted ${plural(t.requests ?? 0, "request", "requests")}.`;
  return analytics
    ? { ...base, detail, state: "done", to: ANALYTICS, linkLabel: "Analytics" }
    : { ...base, detail, state: "done", to: GET_STARTED, linkLabel: "See the steps" };
}

/** Every step that can be done today is done. */
export function setupComplete(items: ChecklistItem[]): boolean {
  return items.every((i) => i.state !== "todo") && items.some((i) => i.state === "done");
}
